const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    // Surface the LLM quota exceeded case with a friendlier toast — imported
    // lazily to avoid a hard dependency in module init.
    if (response.status === 429 && error?.code === 'llm_quota_exceeded') {
      try {
        const { toast } = await import('sonner');
        toast.error(error.detail || 'You have used your 10 daily AI actions. Try again tomorrow.', { duration: 6000 });
      } catch { /* ignore */ }
    }
    // Normalise so consumers can branch on status / structured detail codes
    // (e.g. competitive-exam class-gate) without parsing strings.
    const detail = error?.detail;
    const message = typeof detail === 'string'
      ? detail
      : (detail?.message || 'Request failed');
    const err = new Error(message);
    err.status = response.status;
    err.detail = detail;
    err.code = typeof detail === 'object' ? detail?.code : undefined;
    throw err;
  }
  return response.json();
};

// Schools API
export const schoolsApi = {
  getAll: async () => {
    const res = await fetch(`${API_URL}/schools`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getById: async (id) => {
    const res = await fetch(`${API_URL}/schools/${id}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  create: async (data) => {
    const res = await fetch(`${API_URL}/schools`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  update: async (id, data) => {
    const res = await fetch(`${API_URL}/schools/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  delete: async (id) => {
    const res = await fetch(`${API_URL}/schools/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  getUsers: async (schoolId) => {
    const res = await fetch(`${API_URL}/schools/${schoolId}/users`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getCourses: async (schoolId) => {
    const res = await fetch(`${API_URL}/schools/${schoolId}/courses`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  bulkAssignUsers: async (schoolId, userIds) => {
    const res = await fetch(`${API_URL}/schools/${schoolId}/bulk-assign-users`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ user_ids: userIds }),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  bulkAssignCourses: async (schoolId, courseIds) => {
    const res = await fetch(`${API_URL}/schools/${schoolId}/bulk-assign-courses`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ course_ids: courseIds }),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  removeUser: async (schoolId, userId) => {
    const res = await fetch(`${API_URL}/schools/${schoolId}/remove-user/${userId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Users API
export const usersApi = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_URL}/users?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  updateRole: async (userId, roleData) => {
    const res = await fetch(`${API_URL}/users/${userId}/role`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(roleData),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  update: async (userId, data) => {
    const res = await fetch(`${API_URL}/users/${userId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  // Phase 40.33 — admin user management (Add | Delete | Reset PW | Notify)
  create: async (payload) => {
    const res = await fetch(`${API_URL}/admin/users`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify(payload), credentials: 'include',
    });
    return handleResponse(res);
  },
  remove: async (userId) => {
    const res = await fetch(`${API_URL}/admin/users/${userId}`, {
      method: 'DELETE', headers: getHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },
  resetPassword: async (userId, newPassword = null, notifyEmail = true) => {
    const res = await fetch(`${API_URL}/admin/users/${userId}/reset-password`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ new_password: newPassword, notify_email: notifyEmail }),
      credentials: 'include',
    });
    return handleResponse(res);
  },
  notify: async (userId, subject, message) => {
    const res = await fetch(`${API_URL}/admin/users/${userId}/notify`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ subject, message }), credentials: 'include',
    });
    return handleResponse(res);
  },
};

// Phase 39.6/39.7 — Recent Sign-Ins / session management / LLM kill switch
export const sessionsApi = {
  list: async () => {
    const res = await fetch(`${API_URL}/auth/sessions`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  revokeOthers: async () => {
    const res = await fetch(`${API_URL}/auth/sessions/revoke-others`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },
};

export const llmStatusApi = {
  get: async () => {
    const res = await fetch(`${API_URL}/admin/llm-status`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  setActive: async (active) => {
    const res = await fetch(`${API_URL}/admin/llm-status`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ active }),
    });
    return handleResponse(res);
  },
};

// Courses API
export const coursesApi = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_URL}/courses?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getById: async (id) => {
    const res = await fetch(`${API_URL}/courses/${id}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getChapterTitles: async (id) => {
    const res = await fetch(`${API_URL}/courses/${id}/chapter-titles`, { credentials: 'include' });
    return handleResponse(res);
  },
  getChaptersTranslated: async (id, lang = 'en') => {
    const res = await fetch(`${API_URL}/courses/${id}/chapters-translated?lang=${lang}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  translateChapter: async (id, order, lang) => {
    const res = await fetch(`${API_URL}/courses/${id}/chapter-translate?order=${order}&lang=${lang}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getDescription: async (id) => {
    const res = await fetch(`${API_URL}/courses/${id}/description`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getCertificate: async (id) => {
    const res = await fetch(`${API_URL}/certificates/${id}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  updateDescription: async (id, data) => {
    const res = await fetch(`${API_URL}/courses/${id}/description`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  create: async (data) => {
    const res = await fetch(`${API_URL}/courses`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  update: async (id, data) => {
    const res = await fetch(`${API_URL}/courses/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  delete: async (id) => {
    const res = await fetch(`${API_URL}/courses/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Enrollments Management API
export const enrollmentsApi = {
  getAll: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    const res = await fetch(`${API_URL}/admin/enrollments${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  updateStatus: async (requestId, status) => {
    const res = await fetch(`${API_URL}/course-requests/${requestId}/status`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ status }),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Chapters API
export const chaptersApi = {
  getById: async (id) => {
    const res = await fetch(`${API_URL}/chapters/${id}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getByCourse: async (courseId) => {
    const res = await fetch(`${API_URL}/courses/${courseId}/chapters`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  create: async (data) => {
    const res = await fetch(`${API_URL}/chapters`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  update: async (id, data) => {
    const res = await fetch(`${API_URL}/chapters/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Course Requests API
export const courseRequestsApi = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_URL}/course-requests?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getMyCourses: async () => {
    const res = await fetch(`${API_URL}/my-courses`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  create: async (courseId) => {
    const res = await fetch(`${API_URL}/course-requests`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ course_id: courseId }),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  update: async (requestId, data) => {
    const res = await fetch(`${API_URL}/course-requests/${requestId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Progress API
export const progressApi = {
  get: async (courseId) => {
    const res = await fetch(`${API_URL}/progress/${courseId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  update: async (chapterId, completed) => {
    const res = await fetch(`${API_URL}/progress`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ chapter_id: chapterId, completed }),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Exam API
export const examsApi = {
  get: async (courseId) => {
    const res = await fetch(`${API_URL}/exams/${courseId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  start: async (courseId) => {
    const res = await fetch(`${API_URL}/exams/${courseId}/start`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  submit: async (courseId, examId, answers) => {
    const res = await fetch(`${API_URL}/exams/${courseId}/submit`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ exam_id: examId, answers }),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  getAttempts: async (courseId) => {
    const res = await fetch(`${API_URL}/exams/${courseId}/attempts`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  }
};

// Code Execution API
export const executeApi = {
  run: async (language, code, chapterId = null) => {
    const res = await fetch(`${API_URL}/execute`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ language, code, chapter_id: chapterId }),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Stats API
export const statsApi = {
  getDashboard: async () => {
    const res = await fetch(`${API_URL}/stats/dashboard`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  }
};

// Notifications API
export const notificationsApi = {
  get: async () => {
    const res = await fetch(`${API_URL}/notifications`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  markAllRead: async () => {
    const res = await fetch(`${API_URL}/notifications/mark-all-read`, { method: 'POST', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  }
};

// Seed API
export const seedApi = {
  seed: async () => {
    const res = await fetch(`${API_URL}/seed`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Admin API
export const adminApi = {
  // Organization Growth
  getOrganizationGrowth: async (schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const res = await fetch(`${API_URL}/admin/organization-growth${params}`, { 
      headers: getHeaders(), 
      credentials: 'include' 
    });
    return handleResponse(res);
  },
  
  // Exam Dashboard
  getExamDashboard: async (schoolId = null) => {
    const params = schoolId ? `?school_id=${schoolId}` : '';
    const res = await fetch(`${API_URL}/admin/exam-dashboard${params}`, { 
      headers: getHeaders(), 
      credentials: 'include' 
    });
    return handleResponse(res);
  },
  
  // System Panel
  getSystemPanel: async () => {
    const res = await fetch(`${API_URL}/admin/system-panel`, { 
      headers: getHeaders(), 
      credentials: 'include' 
    });
    return handleResponse(res);
  },
  
  // Bulk Delete Users
  bulkDeleteUsers: async (userIds) => {
    const res = await fetch(`${API_URL}/admin/bulk-delete-users`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ user_ids: userIds }),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  
  // Bulk Delete Courses
  bulkDeleteCourses: async (courseIds) => {
    const res = await fetch(`${API_URL}/admin/bulk-delete-courses`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ course_ids: courseIds }),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  
  // Hard Delete Courses
  hardDeleteCourses: async (courseIds) => {
    const res = await fetch(`${API_URL}/admin/hard-delete-courses`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ course_ids: courseIds }),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  
  // Cleanup Orphaned Resources
  cleanupOrphaned: async () => {
    const res = await fetch(`${API_URL}/admin/cleanup-orphaned`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  },

  // Feature Flags
  getFeatureFlags: async () => {
    const res = await fetch(`${API_URL}/admin/feature-flags`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  toggleFeatureFlag: async (flagId, enabled) => {
    const res = await fetch(`${API_URL}/admin/feature-flags/${flagId}`, {
      method: 'PUT', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ enabled }),
    });
    return handleResponse(res);
  },
  getFeatureFlagsStatus: async () => {
    const res = await fetch(`${API_URL}/feature-flags/status`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Question Bank
  getQuestionBankStats: async () => {
    const res = await fetch(`${API_URL}/admin/question-bank/stats`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  generateQuestionBank: async (board, grade, subject, count = 30) => {
    const res = await fetch(`${API_URL}/admin/question-bank/generate`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ board, grade, subject, count }),
    });
    return handleResponse(res);
  },
  clearQuestionBank: async (board, grade, subject) => {
    const res = await fetch(`${API_URL}/admin/question-bank/clear`, {
      method: 'DELETE', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ board, grade, subject }),
    });
    return handleResponse(res);
  },

  // Chapter Weightage
  getChapterWeightage: async (board, grade, subject) => {
    const params = new URLSearchParams({ board, grade: String(grade), subject });
    const res = await fetch(`${API_URL}/admin/chapter-weightage?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  updateChapterWeightage: async (board, grade, subject, chapters) => {
    const res = await fetch(`${API_URL}/admin/chapter-weightage`, {
      method: 'PUT', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ board, grade, subject, chapters }),
    });
    return handleResponse(res);
  },
};

// Gamification API
export const gamificationApi = {
  getMyStats: async () => {
    const res = await fetch(`${API_URL}/gamification/my-stats`, { 
      headers: getHeaders(), 
      credentials: 'include' 
    });
    return handleResponse(res);
  },
  
  getLeaderboard: async (scope = 'global', limit = 20) => {
    const res = await fetch(`${API_URL}/gamification/leaderboard?scope=${scope}&limit=${limit}`, { 
      headers: getHeaders(), 
      credentials: 'include' 
    });
    return handleResponse(res);
  }
};

// Course Authoring API
export const courseAuthoringApi = {
  createChapter: async (courseId, chapterData) => {
    const res = await fetch(`${API_URL}/courses/${courseId}/chapters`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(chapterData),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  
  updateChapter: async (chapterId, updates) => {
    const res = await fetch(`${API_URL}/chapters/${chapterId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(updates),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  
  deleteChapter: async (chapterId) => {
    const res = await fetch(`${API_URL}/chapters/${chapterId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  
  getCourseExam: async (courseId) => {
    const res = await fetch(`${API_URL}/courses/${courseId}/exam`, { 
      headers: getHeaders(), 
      credentials: 'include' 
    });
    return handleResponse(res);
  },
  
  updateCourseExam: async (courseId, examData) => {
    const res = await fetch(`${API_URL}/courses/${courseId}/exam`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(examData),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  
  addExamQuestion: async (courseId, question) => {
    const res = await fetch(`${API_URL}/courses/${courseId}/exam/questions`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(question),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  
  deleteExamQuestion: async (courseId, questionIndex) => {
    const res = await fetch(`${API_URL}/courses/${courseId}/exam/questions/${questionIndex}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Grade Allocations API
export const gradeAllocationsApi = {
  get: async () => {
    const res = await fetch(`${API_URL}/grade-allocations`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  update: async (data) => {
    const res = await fetch(`${API_URL}/grade-allocations`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  autoGenerate: async () => {
    const res = await fetch(`${API_URL}/grade-allocations/auto-generate`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  getStudentContent: async () => {
    const res = await fetch(`${API_URL}/student/allowed-content`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  }
};

// Grade Change Requests API
export const gradeChangeRequestApi = {
  create: async (data) => {
    const res = await fetch(`${API_URL}/grade-change-requests`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  getMine: async () => {
    const res = await fetch(`${API_URL}/my-grade-requests`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getAll: async () => {
    const res = await fetch(`${API_URL}/grade-change-requests`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  approve: async (requestId) => {
    const res = await fetch(`${API_URL}/grade-change-requests/${requestId}/approve`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  reject: async (requestId, reason) => {
    const res = await fetch(`${API_URL}/grade-change-requests/${requestId}/reject`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ reason }),
      credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Lab Progress API
export const labProgressApi = {
  completeMission: async (data) => {
    const res = await fetch(`${API_URL}/lab-progress/complete`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(res);
  },
  getAll: async () => {
    const res = await fetch(`${API_URL}/lab-progress`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getStats: async () => {
    const res = await fetch(`${API_URL}/lab-progress/stats`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getAnalytics: async () => {
    const res = await fetch(`${API_URL}/admin/lab-analytics`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getClassOverview: async () => {
    const res = await fetch(`${API_URL}/lab-progress/class-overview`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getGamificationStats: async () => {
    const res = await fetch(`${API_URL}/lab-gamification-stats`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getGamificationLeaderboard: async () => {
    const res = await fetch(`${API_URL}/lab-gamification-leaderboard`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  }
};

export const ttsApi = {
  generate: async (text, cacheKey, language = 'en', languageName = 'English') => {
    const res = await fetch(`${API_URL}/tts/generate`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ text, cache_key: cacheKey, language, language_name: languageName }), credentials: 'include'
    });
    return handleResponse(res);
  },
  prelearn: async (labs, language = 'en', languageName = 'English') => {
    const res = await fetch(`${API_URL}/tts/prelearn`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ labs, language, language_name: languageName }), credentials: 'include'
    });
    return handleResponse(res);
  },
  prelearnStatus: async () => {
    const res = await fetch(`${API_URL}/tts/prelearn-status`, {
      headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  }
};

export const videoApi = {
  generate: async (prompt, labId, duration = 4) => {
    const res = await fetch(`${API_URL}/video/generate`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ prompt, lab_id: labId, duration }), credentials: 'include'
    });
    return handleResponse(res);
  }
};

// Board Exams API (P03)
export const boardExamsApi = {
  getSubjects: async (board, grade, stream = null) => {
    const params = new URLSearchParams({ board, grade: String(grade) });
    if (stream) params.append('stream', stream);
    const res = await fetch(`${API_URL}/board-exams/subjects?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  generate: async (data) => {
    const res = await fetch(`${API_URL}/board-exams/generate`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify(data), credentials: 'include'
    });
    return handleResponse(res);
  },
  submit: async (data) => {
    const res = await fetch(`${API_URL}/board-exams/submit`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify(data), credentials: 'include'
    });
    return handleResponse(res);
  },
  getHistory: async () => {
    const res = await fetch(`${API_URL}/board-exams/history`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

// Board Exam Simulator API
export const boardExamSimulatorApi = {
  getGapAnalysis: async () => {
    const res = await fetch(`${API_URL}/board-exam-simulator/gap-analysis`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getTemplate: async (board, grade) => {
    const params = new URLSearchParams({ board, grade: String(grade) });
    const res = await fetch(`${API_URL}/board-exam-simulator/template?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  generate: async (board, grade, subject) => {
    const params = new URLSearchParams({ board, grade: String(grade), subject });
    const res = await fetch(`${API_URL}/board-exam-simulator/generate?${params}`, {
      method: 'POST', headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  },
  submit: async (data) => {
    const res = await fetch(`${API_URL}/board-exam-simulator/submit`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify(data), credentials: 'include'
    });
    return handleResponse(res);
  },
  generateQuick: async (board, grade, subject, count = 10) => {
    const params = new URLSearchParams({ board, grade: String(grade), subject, count: String(count) });
    const res = await fetch(`${API_URL}/board-exam-simulator/generate-quick?${params}`, {
      method: 'POST', headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  },
  generateRealExam: async (board, grade, subject) => {
    const params = new URLSearchParams({ board, grade: String(grade), subject });
    const res = await fetch(`${API_URL}/board-exam-simulator/generate-real-exam?${params}`, {
      method: 'POST', headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  },
};

// Competitive Exams API (P04)
export const competitiveExamsApi = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_URL}/competitive-exams?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getCategories: async () => {
    const res = await fetch(`${API_URL}/competitive-exams/categories`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getById: async (examId) => {
    const res = await fetch(`${API_URL}/competitive-exams/${examId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  generatePractice: async (examId, subject = null, count = 10, year = null) => {
    const params = new URLSearchParams({ count: String(count) });
    if (subject) params.append('subject', subject);
    if (year) params.append('year', String(year));
    const res = await fetch(`${API_URL}/competitive-exams/${examId}/generate-practice?${params}`, {
      method: 'POST', headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  },
  generateRealExam: async (examId) => {
    const res = await fetch(`${API_URL}/competitive-exams/${examId}/generate-real-exam`, {
      method: 'POST', headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  },
  generateMock: async (examId, year = null) => {
    const params = year ? `?year=${year}` : '';
    const res = await fetch(`${API_URL}/competitive-exams/${examId}/generate-mock${params}`, {
      method: 'POST', headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  },
  pollMockJob: async (jobId) => {
    const res = await fetch(`${API_URL}/competitive-exams/mock-jobs/${jobId}`, {
      headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  },
  generateCustom: async (examId, payload) => {
    const res = await fetch(`${API_URL}/competitive-exams/${examId}/generate-custom`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(payload), credentials: 'include'
    });
    return handleResponse(res);
  },
  getWeightage: async (examId) => {
    const res = await fetch(`${API_URL}/competitive-exams/${examId}/weightage`, {
      headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  },
  submitScore: async (examId, data) => {
    const res = await fetch(`${API_URL}/competitive-exams/${examId}/submit-score`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(data), credentials: 'include'
    });
    return handleResponse(res);
  },
  getLeaderboard: async (examId, mode = null) => {
    const params = mode ? `?mode=${mode}` : '';
    const res = await fetch(`${API_URL}/competitive-exams/${examId}/leaderboard${params}`, {
      headers: getHeaders(), credentials: 'include'
    });
    return handleResponse(res);
  },
};

export const analyticsApi = {
  getMyPerformance: async () => {
    const res = await fetch(`${API_URL}/analytics/my-performance`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getClassOverview: async () => {
    const res = await fetch(`${API_URL}/analytics/class-overview`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getCompetitiveOverview: async (examId = null, days = 30) => {
    const params = new URLSearchParams({ days: String(days) });
    if (examId) params.append('exam_id', examId);
    const res = await fetch(`${API_URL}/analytics/competitive/overview?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

// Error Book API — Phase 31
export const errorBookApi = {
  log: async (data) => {
    try {
      const res = await fetch(`${API_URL}/error-book/log`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify(data), credentials: 'include'
      });
      return handleResponse(res);
    } catch (_e) { return null; } // logging is fire-and-forget — never break the user flow
  },
  list: async ({ examId, subject, topic, resolved, source, limit } = {}) => {
    const params = new URLSearchParams();
    if (examId) params.append('exam_id', examId);
    if (subject) params.append('subject', subject);
    if (topic) params.append('topic', topic);
    if (resolved !== undefined) params.append('resolved', String(resolved));
    if (source) params.append('source', source);
    if (limit) params.append('limit', String(limit));
    const res = await fetch(`${API_URL}/error-book?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  resolve: async (errorId) => {
    const res = await fetch(`${API_URL}/error-book/${errorId}/resolve`, { method: 'PATCH', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  remove: async (errorId) => {
    const res = await fetch(`${API_URL}/error-book/${errorId}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  bulkResolve: async (filter = {}) => {
    const res = await fetch(`${API_URL}/error-book/bulk-resolve`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(filter), credentials: 'include'
    });
    return handleResponse(res);
  },
  rePractice: async (examId = null, limit = 10) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (examId) params.append('exam_id', examId);
    const res = await fetch(`${API_URL}/error-book/re-practice?${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

// Topic Progress API — Phase 31
export const topicProgressApi = {
  mark: async (data) => {
    try {
      const res = await fetch(`${API_URL}/topic-progress/mark`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify(data), credentials: 'include'
      });
      return handleResponse(res);
    } catch (_e) { return null; }
  },
  get: async (examId) => {
    const res = await fetch(`${API_URL}/topic-progress/${examId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

// DPP & Streaks & Exam Countdown — Phase 33
export const dppApi = {
  today: async (examId) => {
    const res = await fetch(`${API_URL}/dpp/today/${examId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getSubject: async (examId, subject) => {
    const res = await fetch(`${API_URL}/dpp/subject/${examId}/${encodeURIComponent(subject)}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  submit: async (dppId, answers) => {
    const res = await fetch(`${API_URL}/dpp/${dppId}/attempt`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ answers }), credentials: 'include'
    });
    return handleResponse(res);
  },
  streak: async (examId) => {
    const res = await fetch(`${API_URL}/dpp/streak/${examId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  history: async (examId, days = 30) => {
    const res = await fetch(`${API_URL}/dpp/history/${examId}?days=${days}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

export const countdownApi = {
  forExam: async (examId) => {
    const res = await fetch(`${API_URL}/exam-countdown/${examId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  all: async () => {
    const res = await fetch(`${API_URL}/exam-countdown`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

// Revision Tools — Phase 34
export const revisionApi = {
  formulaBook: async (examId) => {
    const res = await fetch(`${API_URL}/revision/formula-book/${examId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  notesIndex: async (examId) => {
    const res = await fetch(`${API_URL}/revision/notes-index/${examId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getNote: async (examId, topic, subject = null) => {
    const params = subject ? `?subject=${encodeURIComponent(subject)}` : '';
    const res = await fetch(`${API_URL}/revision/notes/${examId}/${encodeURIComponent(topic)}${params}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  generateFlashcards: async (examId) => {
    const res = await fetch(`${API_URL}/revision/flashcards/generate/${examId}`, { method: 'POST', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  dueFlashcards: async (examId, limit = 20) => {
    const res = await fetch(`${API_URL}/revision/flashcards/due/${examId}?limit=${limit}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  reviewFlashcard: async (cardId, quality) => {
    const res = await fetch(`${API_URL}/revision/flashcards/${cardId}/review`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ quality }), credentials: 'include'
    });
    return handleResponse(res);
  },
  flashcardStats: async (examId) => {
    const res = await fetch(`${API_URL}/revision/flashcards/${examId}/stats`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

// Doubt Chat API (YouTube curation + PDF export removed Feb 2026)
export const doubtChatApi = {
  listSessions: async (examId = null) => {
    const qs = examId ? `?exam_id=${examId}` : '';
    const res = await fetch(`${API_URL}/doubt/sessions${qs}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  createSession: async (examId, topic = '', subject = '', title = '') => {
    const res = await fetch(`${API_URL}/doubt/sessions`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ exam_id: examId, topic, subject, title }),
    });
    return handleResponse(res);
  },
  getSession: async (sessionId) => {
    const res = await fetch(`${API_URL}/doubt/sessions/${sessionId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  sendMessage: async (sessionId, text) => {
    const res = await fetch(`${API_URL}/doubt/sessions/${sessionId}/message`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ text }),
    });
    return handleResponse(res);
  },
  deleteSession: async (sessionId) => {
    const res = await fetch(`${API_URL}/doubt/sessions/${sessionId}`, {
      method: 'DELETE', headers: getHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },
};

// Phase 35.1 — AI-generated topic videos (admin generate + student playback)
export const topicVideoApi = {
  get: async (examId, topic) => {
    const res = await fetch(`${API_URL}/topic-video/${examId}/${encodeURIComponent(topic)}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  // Phase 38.7 — full curriculum list (intro + deep-dives) for a topic
  list: async (examId, topic) => {
    const res = await fetch(`${API_URL}/topic-video-list/${examId}/${encodeURIComponent(topic)}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  adminCurriculum: async (examId, topic, { subject = null, count = 5, regenerate = false } = {}) => {
    const res = await fetch(`${API_URL}/admin/topic-video/curriculum`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ exam_id: examId, topic, subject, count, regenerate }),
    });
    return handleResponse(res);
  },
  fileUrl: (path) => `${process.env.REACT_APP_BACKEND_URL}/api/topic-video-file/${path}`,
  // stream video as blob URL so Authorization header is honoured
  fetchBlobUrl: async (path) => {
    const res = await fetch(`${API_URL}/topic-video-file/${path}`, { headers: getHeaders(), credentials: 'include' });
    if (!res.ok) throw new Error('Video unavailable');
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
  // admin
  adminGenerate: async (examId, topic, subject = null) => {
    const res = await fetch(`${API_URL}/admin/topic-video/generate`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ exam_id: examId, topic, subject }),
    });
    return handleResponse(res);
  },
  adminJobs: async (examId = null) => {
    const qs = examId ? `?exam_id=${examId}` : '';
    const res = await fetch(`${API_URL}/admin/topic-video/jobs${qs}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  adminJob: async (jobId) => {
    const res = await fetch(`${API_URL}/admin/topic-video/jobs/${jobId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  adminCancel: async (jobId) => {
    const res = await fetch(`${API_URL}/admin/topic-video/jobs/${jobId}/cancel`, { method: 'POST', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  adminSeedPriority: async (examId, count = 20, concurrency = 3) => {
    const res = await fetch(`${API_URL}/admin/topic-video/seed-priority`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ exam_id: examId, count, concurrency }),
    });
    return handleResponse(res);
  },
  adminRestitch: async ({ examId = null, limit = 200, concurrency = 3 } = {}) => {
    const body = { limit, concurrency };
    if (examId) body.exam_id = examId;
    const res = await fetch(`${API_URL}/admin/topic-video/restitch`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify(body),
    });
    return handleResponse(res);
  },
  adminExportVideos: async (examId = null) => {
    const url = examId
      ? `${API_URL}/admin/topic-video/export?exam_id=${encodeURIComponent(examId)}`
      : `${API_URL}/admin/topic-video/export`;
    const res = await fetch(url, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  adminImportVideos: async (payload) => {
    const res = await fetch(`${API_URL}/admin/topic-video/import`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify(payload),
    });
    return handleResponse(res);
  },
  // Auto-render worker controls
  workerStatus: async () => {
    const res = await fetch(`${API_URL}/admin/auto-render/status`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  workerStart: async (concurrency = null) => {
    const res = await fetch(`${API_URL}/admin/auto-render/start`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify(concurrency ? { concurrency } : {}),
    });
    return handleResponse(res);
  },
  workerStop: async () => {
    const res = await fetch(`${API_URL}/admin/auto-render/stop`, { method: 'POST', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  workerRestart: async () => {
    const res = await fetch(`${API_URL}/admin/auto-render/restart`, { method: 'POST', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  workerConcurrency: async (value) => {
    const res = await fetch(`${API_URL}/admin/auto-render/concurrency`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ value }),
    });
    return handleResponse(res);
  },
  // Phase 38.3 — recover stale_timeout failures
  adminRequeueFailed: async ({ error = 'stale_timeout', examId = null, limit = 500 } = {}) => {
    const body = { error, limit };
    if (examId) body.exam_id = examId;
    const res = await fetch(`${API_URL}/admin/topic-video/requeue-failed`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify(body),
    });
    return handleResponse(res);
  },
  // Phase 38.4 — drain history (per-hour throughput)
  workerThroughput: async (hours = 24) => {
    const res = await fetch(`${API_URL}/admin/auto-render/throughput?hours=${hours}`, {
      headers: getHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },
};

// Phase 36 — Rank & College Predictor
export const predictorApi = {
  colleges: async (examId) => {
    const res = await fetch(`${API_URL}/predictor/colleges/${examId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  predict: async ({ examId, score, percentile }) => {
    const res = await fetch(`${API_URL}/predictor/predict`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({
        exam_id: examId,
        ...(score !== undefined && score !== null && score !== '' ? { score: Number(score) } : {}),
        ...(percentile !== undefined && percentile !== null && percentile !== '' ? { percentile: Number(percentile) } : {}),
      }),
    });
    return handleResponse(res);
  },
  history: async (examId = null) => {
    const qs = examId ? `?exam_id=${examId}` : '';
    const res = await fetch(`${API_URL}/predictor/history${qs}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

// Phase 36.2 — Sora 2 concept reels
export const conceptReelApi = {
  get: async (examId, topic) => {
    const res = await fetch(`${API_URL}/concept-reel/${examId}/${encodeURIComponent(topic)}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  adminGenerate: async (examId, topic, { duration = 8, model = 'sora-2' } = {}) => {
    const res = await fetch(`${API_URL}/admin/concept-reel/generate`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ exam_id: examId, topic, duration, model }),
    });
    return handleResponse(res);
  },
  adminJobs: async (examId = null) => {
    const qs = examId ? `?exam_id=${examId}` : '';
    const res = await fetch(`${API_URL}/admin/concept-reel/jobs${qs}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  adminSeedPriority: async (examIds = ['jee_main', 'neet_ug'], count = 10, duration = 8) => {
    const res = await fetch(`${API_URL}/admin/concept-reel/seed-priority`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ exam_ids: examIds, count, duration }),
    });
    return handleResponse(res);
  },
};

// Phase 36.3 — AITS
export const aitsApi = {
  list: async (examId = null) => {
    const qs = examId ? `?exam_id=${examId}` : '';
    const res = await fetch(`${API_URL}/aits/list${qs}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  start: async (testId) => {
    const res = await fetch(`${API_URL}/aits/${testId}/start`, { method: 'POST', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  submit: async (testId, answers) => {
    const res = await fetch(`${API_URL}/aits/${testId}/submit`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ answers }),
    });
    return handleResponse(res);
  },
  leaderboard: async (testId, limit = 50) => {
    const res = await fetch(`${API_URL}/aits/${testId}/leaderboard?limit=${limit}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  myAttempt: async (testId) => {
    const res = await fetch(`${API_URL}/aits/${testId}/my-attempt`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  adminSchedule: async ({ examId, title, startsAt, durationMin = 90, totalQuestions = 30 }) => {
    const res = await fetch(`${API_URL}/admin/aits/schedule`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({
        exam_id: examId, title, starts_at: startsAt,
        duration_min: durationMin, total_questions: totalQuestions,
      }),
    });
    return handleResponse(res);
  },
  adminList: async () => {
    const res = await fetch(`${API_URL}/admin/aits/tests`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  adminDelete: async (testId) => {
    const res = await fetch(`${API_URL}/admin/aits/tests/${testId}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

// Phase 37 — Daily Learning Plan
export const studyPlanApi = {
  current: async () => {
    const res = await fetch(`${API_URL}/study-plan/current`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  create: async (payload) => {
    const res = await fetch(`${API_URL}/study-plan/create`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify(payload),
    });
    return handleResponse(res);
  },
  remove: async () => {
    const res = await fetch(`${API_URL}/study-plan/current`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  day: async (d) => {
    const res = await fetch(`${API_URL}/study-plan/day/${d}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  month: async (yyyyMm) => {
    const res = await fetch(`${API_URL}/study-plan/month/${yyyyMm}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  complete: async (d, blockId) => {
    const res = await fetch(`${API_URL}/study-plan/complete`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ date: d, block_id: blockId }),
    });
    return handleResponse(res);
  },
  uncomplete: async (d, blockId) => {
    const res = await fetch(`${API_URL}/study-plan/uncomplete`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ date: d, block_id: blockId }),
    });
    return handleResponse(res);
  },
  streak: async () => {
    const res = await fetch(`${API_URL}/study-plan/streak`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  refreshPool: async () => {
    const res = await fetch(`${API_URL}/study-plan/refresh-pool`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
    });
    return handleResponse(res);
  },
  reminder: async () => {
    const res = await fetch(`${API_URL}/study-plan/reminder`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};

// Classroom API
export const classroomApi = {
  getClasses: async () => {
    const res = await fetch(`${API_URL}/classroom/classes`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getClass: async (classId) => {
    const res = await fetch(`${API_URL}/classroom/classes/${classId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  createClass: async (data) => {
    const res = await fetch(`${API_URL}/classroom/classes`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(data), credentials: 'include' });
    return handleResponse(res);
  },
  joinClass: async (code) => {
    const res = await fetch(`${API_URL}/classroom/classes/join`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ class_code: code }), credentials: 'include' });
    return handleResponse(res);
  },
  deleteClass: async (classId) => {
    const res = await fetch(`${API_URL}/classroom/classes/${classId}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  getStream: async (classId) => {
    const res = await fetch(`${API_URL}/classroom/classes/${classId}/stream`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  postToStream: async (classId, content) => {
    const res = await fetch(`${API_URL}/classroom/classes/${classId}/stream`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ content }), credentials: 'include' });
    return handleResponse(res);
  },
  addComment: async (postId, content) => {
    const res = await fetch(`${API_URL}/classroom/stream/${postId}/comments`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ content }), credentials: 'include' });
    return handleResponse(res);
  },
  getAssignments: async (classId) => {
    const res = await fetch(`${API_URL}/classroom/classes/${classId}/assignments`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  createAssignment: async (data) => {
    const res = await fetch(`${API_URL}/classroom/assignments`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(data), credentials: 'include' });
    return handleResponse(res);
  },
  getAssignment: async (assignmentId) => {
    const res = await fetch(`${API_URL}/classroom/assignments/${assignmentId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  submitAssignment: async (assignmentId, data) => {
    const res = await fetch(`${API_URL}/classroom/assignments/${assignmentId}/submit`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(data), credentials: 'include' });
    return handleResponse(res);
  },
  getSubmissions: async (assignmentId) => {
    const res = await fetch(`${API_URL}/classroom/assignments/${assignmentId}/submissions`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  gradeSubmission: async (submissionId, data) => {
    const res = await fetch(`${API_URL}/classroom/submissions/${submissionId}/grade`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(data), credentials: 'include' });
    return handleResponse(res);
  },
  getGrades: async (classId) => {
    const res = await fetch(`${API_URL}/classroom/grades/${classId}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
};



// Documents API
export const docsApi = {
  list: async () => { const r = await fetch(`${API_URL}/docs`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  get: async (id) => { const r = await fetch(`${API_URL}/docs/${id}`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  create: async (d) => { const r = await fetch(`${API_URL}/docs`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  update: async (id, d) => { const r = await fetch(`${API_URL}/docs/${id}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  delete: async (id) => { const r = await fetch(`${API_URL}/docs/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  getVersions: async (id) => { const r = await fetch(`${API_URL}/docs/${id}/versions`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  share: async (id, email) => { const r = await fetch(`${API_URL}/docs/${id}/share`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ email }), credentials: 'include' }); return handleResponse(r); },
};

// Drive API
export const driveApi = {
  list: async (parentId) => { const q = parentId ? `?parent_id=${parentId}` : ''; const r = await fetch(`${API_URL}/drive/files${q}`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  create: async (d) => { const r = await fetch(`${API_URL}/drive/files`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  update: async (id, d) => { const r = await fetch(`${API_URL}/drive/files/${id}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  delete: async (id) => { const r = await fetch(`${API_URL}/drive/files/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  share: async (id, email) => { const r = await fetch(`${API_URL}/drive/files/${id}/share`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ email }), credentials: 'include' }); return handleResponse(r); },
};

// Calendar API
export const calendarApi = {
  getEvents: async () => { const r = await fetch(`${API_URL}/calendar/events`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  create: async (d) => { const r = await fetch(`${API_URL}/calendar/events`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  update: async (id, d) => { const r = await fetch(`${API_URL}/calendar/events/${id}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  delete: async (id) => { const r = await fetch(`${API_URL}/calendar/events/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
};

// Chat API
export const chatApi = {
  getConversations: async () => { const r = await fetch(`${API_URL}/chat/conversations`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  createConversation: async (d) => { const r = await fetch(`${API_URL}/chat/conversations`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  getMessages: async (convId, skip = 0) => { const r = await fetch(`${API_URL}/chat/conversations/${convId}/messages?skip=${skip}`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  sendMessage: async (convId, d) => { const r = await fetch(`${API_URL}/chat/conversations/${convId}/messages`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  searchUsers: async (q) => { const r = await fetch(`${API_URL}/chat/users?q=${encodeURIComponent(q)}`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
};

// Email API
export const emailApi = {
  compose: async (d) => { const r = await fetch(`${API_URL}/email/compose`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  getInbox: async () => { const r = await fetch(`${API_URL}/email/inbox`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  getSent: async () => { const r = await fetch(`${API_URL}/email/sent`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  getTrash: async () => { const r = await fetch(`${API_URL}/email/trash`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  get: async (id) => { const r = await fetch(`${API_URL}/email/${id}`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  markRead: async (id) => { const r = await fetch(`${API_URL}/email/${id}/read`, { method: 'PUT', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  trash: async (id) => { const r = await fetch(`${API_URL}/email/${id}/trash`, { method: 'PUT', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  delete: async (id) => { const r = await fetch(`${API_URL}/email/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
};

// Meet API
export const meetApi = {
  getRooms: async () => { const r = await fetch(`${API_URL}/meet/rooms`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  createRoom: async (d) => { const r = await fetch(`${API_URL}/meet/rooms`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  getRoom: async (id) => { const r = await fetch(`${API_URL}/meet/rooms/${id}`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  deleteRoom: async (id) => { const r = await fetch(`${API_URL}/meet/rooms/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  endRoom: async (id) => { const r = await fetch(`${API_URL}/meet/rooms/${id}/end`, { method: 'PUT', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
};

// Sheets API
export const sheetsApi = {
  list: async () => { const r = await fetch(`${API_URL}/sheets`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  get: async (id) => { const r = await fetch(`${API_URL}/sheets/${id}`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  create: async (d) => { const r = await fetch(`${API_URL}/sheets`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  update: async (id, d) => { const r = await fetch(`${API_URL}/sheets/${id}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  delete: async (id) => { const r = await fetch(`${API_URL}/sheets/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  share: async (id, email) => { const r = await fetch(`${API_URL}/sheets/${id}/share`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ email }), credentials: 'include' }); return handleResponse(r); },
};

// Slides API
export const slidesApi = {
  list: async () => { const r = await fetch(`${API_URL}/slides`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  get: async (id) => { const r = await fetch(`${API_URL}/slides/${id}`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  create: async (d) => { const r = await fetch(`${API_URL}/slides`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  update: async (id, d) => { const r = await fetch(`${API_URL}/slides/${id}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  delete: async (id) => { const r = await fetch(`${API_URL}/slides/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  share: async (id, email) => { const r = await fetch(`${API_URL}/slides/${id}/share`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ email }), credentials: 'include' }); return handleResponse(r); },
};

// Whiteboard API
export const whiteboardApi = {
  list: async () => { const r = await fetch(`${API_URL}/whiteboard`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  get: async (id) => { const r = await fetch(`${API_URL}/whiteboard/${id}`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  create: async (d) => { const r = await fetch(`${API_URL}/whiteboard`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  update: async (id, d) => { const r = await fetch(`${API_URL}/whiteboard/${id}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(d), credentials: 'include' }); return handleResponse(r); },
  delete: async (id) => { const r = await fetch(`${API_URL}/whiteboard/${id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  share: async (id, email) => { const r = await fetch(`${API_URL}/whiteboard/${id}/share`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ email }), credentials: 'include' }); return handleResponse(r); },
};

// Suite Hub API
export const suiteHubApi = {
  getData: async () => { const r = await fetch(`${API_URL}/suite/hub`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
};

export const parentApi = {
  linkChild: async (childEmail) => { const r = await fetch(`${API_URL}/parent/link-child`, { method: 'POST', headers: getHeaders(), credentials: 'include', body: JSON.stringify({ child_email: childEmail }) }); return handleResponse(r); },
  unlinkChild: async (childUserId) => { const r = await fetch(`${API_URL}/parent/unlink-child`, { method: 'DELETE', headers: getHeaders(), credentials: 'include', body: JSON.stringify({ child_user_id: childUserId }) }); return handleResponse(r); },
  getChildren: async () => { const r = await fetch(`${API_URL}/parent/children`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
  getChildProgress: async (childId) => { const r = await fetch(`${API_URL}/parent/child/${childId}/progress`, { headers: getHeaders(), credentials: 'include' }); return handleResponse(r); },
};

// Phase 38.5 — admin-managed parent ↔ child links
export const parentLinksApi = {
  list: async ({ parentId = null, childId = null } = {}) => {
    const qs = new URLSearchParams();
    if (parentId) qs.set('parent_id', parentId);
    if (childId) qs.set('child_id', childId);
    const r = await fetch(`${API_URL}/admin/parent-links?${qs.toString()}`, {
      headers: getHeaders(), credentials: 'include',
    });
    return handleResponse(r);
  },
  create: async ({ parentId, childId }) => {
    const r = await fetch(`${API_URL}/admin/parent-links`, {
      method: 'POST', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ parent_id: parentId, child_id: childId }),
    });
    return handleResponse(r);
  },
  remove: async ({ parentId, childId }) => {
    const r = await fetch(`${API_URL}/admin/parent-links`, {
      method: 'DELETE', headers: getHeaders(), credentials: 'include',
      body: JSON.stringify({ parent_id: parentId, child_id: childId }),
    });
    return handleResponse(r);
  },
  searchStudents: async (q, limit = 20) => {
    const r = await fetch(`${API_URL}/admin/students-search?q=${encodeURIComponent(q)}&limit=${limit}`, {
      headers: getHeaders(), credentials: 'include',
    });
    return handleResponse(r);
  },
  counts: async () => {
    const r = await fetch(`${API_URL}/admin/parent-link-counts`, {
      headers: getHeaders(), credentials: 'include',
    });
    return handleResponse(r);
  },
};


// ==================== CODE SNAPSHOTS (Phase 40.17) ====================
// Per-user per-chapter WIP so User A's edits never bleed into User B.
export const codeSnapshotApi = {
  get: async (courseId, chapterIndex) => {
    const r = await fetch(
      `${API_URL}/code/snapshot?course_id=${encodeURIComponent(courseId)}&chapter_index=${chapterIndex}`,
      { headers: getHeaders(), credentials: 'include' }
    );
    return handleResponse(r);
  },
  save: async (courseId, chapterIndex, code) => {
    const r = await fetch(`${API_URL}/code/snapshot`, {
      method: 'PUT',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ course_id: courseId, chapter_index: chapterIndex, code }),
    });
    return handleResponse(r);
  },
  reset: async (courseId, chapterIndex) => {
    const r = await fetch(
      `${API_URL}/code/snapshot?course_id=${encodeURIComponent(courseId)}&chapter_index=${chapterIndex}`,
      { method: 'DELETE', headers: getHeaders(), credentials: 'include' }
    );
    return handleResponse(r);
  },
  // Phase 40.19d — last-5-versions history.
  history: async (courseId, chapterIndex) => {
    const r = await fetch(
      `${API_URL}/code/snapshot/history?course_id=${encodeURIComponent(courseId)}&chapter_index=${chapterIndex}`,
      { headers: getHeaders(), credentials: 'include' }
    );
    return handleResponse(r);
  },
  restore: async (courseId, chapterIndex, historyIndex) => {
    const r = await fetch(`${API_URL}/code/snapshot/restore`, {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ course_id: courseId, chapter_index: chapterIndex, history_index: historyIndex }),
    });
    return handleResponse(r);
  },
};



